import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, Calendar, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import mockApi from '../services/mockApi';

const ClienteNotificacionesPage = () => {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroLeida, setFiltroLeida] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');

  const cargarNotificaciones = useCallback(async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const response = await mockApi.getNotificacionesCliente(clienteRes.data.id);
      setNotificaciones(response.data.notificaciones || []);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    cargarNotificaciones();
  }, [cargarNotificaciones]);

  const marcarComoLeida = async (notificacionId) => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.marcarNotificacionLeida(clienteRes.data.id, notificacionId);
      setNotificaciones(notificaciones.map(n =>
        n.id === notificacionId ? { ...n, leida: true } : n
      ));
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const marcarTodasComoLeidas = async () => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const noLeidas = notificaciones.filter(n => !n.leida);
      for (const notif of noLeidas) {
        await mockApi.marcarNotificacionLeida(clienteRes.data.id, notif.id);
      }
      setNotificaciones(notificaciones.map(n => ({ ...n, leida: true })));
    } catch (error) {
      console.error('Error al marcar todas:', error);
    }
  };

  const notificacionesFiltradas = notificaciones.filter(notif => {
    const cumpleFiltroLeida =
      filtroLeida === 'todas' ||
      (filtroLeida === 'no_leidas' && !notif.leida) ||
      (filtroLeida === 'leidas' && notif.leida);

    const cumpleFiltroTipo = filtroTipo === 'todos' || notif.tipo === filtroTipo;

    return cumpleFiltroLeida && cumpleFiltroTipo;
  });

  const estadisticas = {
    total: notificaciones.length,
    noLeidas: notificaciones.filter(n => !n.leida).length,
    leidas: notificaciones.filter(n => n.leida).length,
    importantes: notificaciones.filter(n => n.tipo === 'alerta').length
  };

  const getTipoConfig = (tipo) => {
    const configs = {
      info: {
        color: 'bg-blue-100 text-blue-700 border-blue-300',
        icon: Info,
        label: 'Información',
        bgGradient: 'from-blue-50 to-blue-100'
      },
      alerta: {
        color: 'bg-red-100 text-red-700 border-red-300',
        icon: AlertCircle,
        label: 'Alerta',
        bgGradient: 'from-red-50 to-red-100'
      },
      exito: {
        color: 'bg-green-100 text-green-700 border-green-300',
        icon: CheckCircle,
        label: 'Éxito',
        bgGradient: 'from-green-50 to-green-100'
      },
      aviso: {
        color: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        icon: AlertTriangle,
        label: 'Aviso',
        bgGradient: 'from-yellow-50 to-yellow-100'
      }
    };
    return configs[tipo] || configs.info;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Notificaciones
            </h1>
            <p className="text-gray-600 mt-1">Mantente informado sobre tu garantía y averías</p>
          </div>
          {estadisticas.noLeidas > 0 && (
            <button
              onClick={marcarTodasComoLeidas}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              <Check className="w-5 h-5" />
              <span className="font-bold">Marcar todas como leídas</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-primary-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Total</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.total}</p>
              </div>
              <Bell className="w-10 h-10 text-primary-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-warning-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">No Leídas</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.noLeidas}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-warning-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-success-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Leídas</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.leidas}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-success-500" />
            </div>
          </div>
          <div className="glass-effect rounded-2xl p-6 border-l-4 border-danger-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold">Importantes</p>
                <p className="text-3xl font-black text-gray-900 mt-1">{estadisticas.importantes}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-danger-500" />
            </div>
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex gap-2">
              <button
                onClick={() => setFiltroLeida('todas')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroLeida === 'todas'
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setFiltroLeida('no_leidas')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroLeida === 'no_leidas'
                    ? 'bg-gradient-to-r from-warning-500 to-warning-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                No Leídas ({estadisticas.noLeidas})
              </button>
              <button
                onClick={() => setFiltroLeida('leidas')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroLeida === 'leidas'
                    ? 'bg-gradient-to-r from-success-500 to-success-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Leídas
              </button>
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setFiltroTipo('todos')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroTipo === 'todos'
                    ? 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos los tipos
              </button>
              <button
                onClick={() => setFiltroTipo('alerta')}
                className={`px-4 py-3 rounded-xl font-bold transition-all ${
                  filtroTipo === 'alerta'
                    ? 'bg-gradient-to-r from-danger-500 to-danger-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Alertas
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {notificacionesFiltradas.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">No hay notificaciones</p>
              </div>
            ) : (
              notificacionesFiltradas.map((notificacion) => {
                const tipoConfig = getTipoConfig(notificacion.tipo);
                const IconoTipo = tipoConfig.icon;

                return (
                  <div
                    key={notificacion.id}
                    className={`glass-effect rounded-xl p-5 hover:shadow-lg transition-all duration-300 ${
                      !notificacion.leida ? 'border-l-4 border-primary-500 bg-gradient-to-r from-primary-50/50 to-transparent' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tipoConfig.color} border-2 flex-shrink-0`}>
                        <IconoTipo className="w-6 h-6" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className={`font-bold text-gray-900 ${!notificacion.leida ? 'text-lg' : ''}`}>
                                {notificacion.titulo}
                              </h3>
                              {!notificacion.leida && (
                                <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
                              )}
                            </div>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${tipoConfig.color}`}>
                              {tipoConfig.label}
                            </span>
                          </div>

                          {!notificacion.leida && (
                            <button
                              onClick={() => marcarComoLeida(notificacion.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-success-500 to-success-600 text-white rounded-lg hover:shadow-md transition-all duration-300 text-sm font-bold"
                            >
                              <Check className="w-4 h-4" />
                              <span>Marcar leída</span>
                            </button>
                          )}
                        </div>

                        <p className="text-gray-700 mb-3">{notificacion.mensaje}</p>

                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(notificacion.fecha).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-start gap-4">
            <Bell className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Sobre las notificaciones</h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• Recibirás notificaciones sobre el estado de tus averías</li>
                <li>• Te avisaremos cuando tu garantía esté próxima a vencer</li>
                <li>• Las alertas importantes requieren tu atención inmediata</li>
                <li>• Puedes configurar tus preferencias de notificaciones en Configuración</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteNotificacionesPage;
